import React from "react";
import { motion } from "framer-motion";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { initialProjects, initialProjectsConfig } from "../data/initialData";
import ProjectPlaceholder from "./ProjectPlaceholder";

const Projects = () => {
  const [projects] = useLocalStorage('projects', initialProjects);
  const [config] = useLocalStorage('projectsConfig', initialProjectsConfig);

  // Treat projects without 'featured' field as web projects (legacy support)
  const featuredProjects = projects.filter(p => p.featured === true);
  const webProjects = projects.filter(p => p.featured !== true);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, duration: 0.6 }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1, y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    }
  };

  const ProjectCard = ({ project, index = 0 }) => (
    <motion.div
      variants={projectVariants}
      whileHover={{ y: -6, transition: { type: "spring", stiffness: 300, damping: 15 } }}
      className="group glass-card rounded-2xl overflow-hidden hover:border-cyan-500/30 hover:shadow-xl hover:shadow-cyan-500/5 transition-all duration-300"
    >
      {/* Image */}
      <motion.div className="relative h-52 overflow-hidden bg-slate-800/50">
        {project.img ? (
          <motion.img
            src={project.img}
            alt={project.name}
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.8 }}
            whileHover={{ scale: 1.05 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <motion.div
            className="w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <ProjectPlaceholder project={project} index={index} />
          </motion.div>
        )}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        />
        <motion.div
          className="absolute top-3 left-3 flex flex-wrap gap-1.5"
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          {project.tech.slice(0, 3).map((tech, i) => (
            <span key={i} className="px-2 py-0.5 bg-slate-900/60 backdrop-blur-sm text-cyan-300/90 text-[10px] rounded-full border border-cyan-500/20 font-mono">
              {tech}
            </span>
          ))}
        </motion.div>
      </motion.div>
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/20 flex items-center justify-center text-lg flex-shrink-0">
            {project.category === 'ai' ? '🧠' : '🌐'}
          </div>
          {project.live_link && (
            <a
              href={project.live_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-cyan-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>

        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
          {project.name}
        </h3>

        {project.description && (
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            {project.description}
          </p>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {project.tech.slice(0, 6).map((tech, i) => (
            <span key={i} className="tech-tag">{tech}</span>
          ))}
          {project.tech.length > 6 && (
            <span className="px-3 py-1 bg-slate-700/50 text-slate-400 text-xs rounded-full font-mono">
              +{project.tech.length - 6}
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <motion.a
            href={project.github_link}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700/70 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-600/50 text-sm"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-.91 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
            <span>Source</span>
          </motion.a>
          {project.live_link && (
            <motion.a
              href={project.live_link}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 hover:from-cyan-500/30 hover:to-blue-600/30 text-cyan-300 rounded-lg transition-all border border-cyan-500/20 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span>Live</span>
            </motion.a>
          )}
        </div>
      </div>
    </motion.div>
  );

  const WebProjectCard = ({ project }) => (
    <motion.div
      variants={projectVariants}
      whileHover={{ y: -4, transition: { type: "spring", stiffness: 300 } }}
      className="group glass-card rounded-xl overflow-hidden hover:border-cyan-500/20 transition-all duration-300"
    >
      <div className="relative h-40 overflow-hidden bg-slate-800/50">
        {project.img && (
          <motion.img
            src={project.img}
            alt={project.name}
            className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.6 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {project.tech.slice(0, 2).map((tech, i) => (
            <span key={i} className="px-2 py-0.5 bg-cyan-500/10 text-cyan-300/80 text-[10px] rounded-full border border-cyan-500/20 font-mono">
              {tech}
            </span>
          ))}
        </div>
      </div>
      <div className="p-5">
        <h3 className="text-sm font-semibold text-white mb-3 group-hover:text-cyan-300 transition-colors">
          {project.name}
        </h3>
        <div className="flex gap-2">
          <motion.a
            href={project.github_link}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex-1 text-center px-3 py-1.5 bg-slate-700/50 hover:bg-slate-700/70 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-600/50 text-xs"
          >
            Code
          </motion.a>
          <motion.a
            href={project.live_link}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="flex-1 text-center px-3 py-1.5 bg-gradient-to-r from-cyan-500/20 to-blue-600/20 hover:from-cyan-500/30 hover:to-blue-600/30 text-cyan-300 rounded-lg transition-all border border-cyan-500/20 text-xs"
          >
            Live
          </motion.a>
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.section
      id="projects"
      className="py-24 relative overflow-hidden bg-slate-950"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={containerVariants}
    >
      <div className="absolute inset-0 bg-grid opacity-20" />

      <motion.div
        className="absolute top-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-[120px]"
        animate={{ x: [0, 80, 0], y: [0, -40, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="section-container relative z-10">
        {/* Featured Projects */}
        <motion.div className="text-center mb-16" variants={containerVariants}>
          <motion.span
            className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-cyan-400 border border-cyan-500/30 rounded-full bg-cyan-500/5"
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
          >
            Featured Work
          </motion.span>
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
          >
            {(() => {
              const words = (config.aiHeading || 'AI & Language Infrastructure').split(' ');
              const last = words.pop();
              return <>{words.join(' ')} <span className="gradient-text">{last}</span></>;
            })()}
          </motion.h2>
          <motion.p
            className="text-slate-400 max-w-2xl mx-auto text-base"
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
          >
            {config.aiDescription}
          </motion.p>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 gap-6 mb-24"
          variants={containerVariants}
        >
          {featuredProjects.map((project, i) => (
            <ProjectCard key={project.id} project={project} index={i} />
          ))}
        </motion.div>

        {/* Web Engineering Projects */}
        <motion.div className="mb-8" variants={itemVariants}>
          <motion.div className="text-center mb-12">
            <motion.span
              className="inline-block px-4 py-1.5 mb-4 text-xs font-mono tracking-wider text-slate-500 border border-slate-700 rounded-full bg-slate-800/30"
            >
              Previous Work
            </motion.span>
            <motion.h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                {(() => {
                  const words = (config.webHeading || 'Web Engineering Projects').split(' ');
                  const last = words.pop();
                  return <>{words.join(' ')} <span className="text-slate-400">{last}</span></>;
                })()}
            </motion.h3>
            <motion.p className="text-slate-500 text-sm">
              {config.webDescription}
            </motion.p>
          </motion.div>
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          variants={containerVariants}
        >
          {webProjects.map(project => (
            <WebProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export default Projects;
